import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import api from '../../services/api';
import Swal from 'sweetalert2';
import './Salary.css';

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
}).format(amount || 0);

const monthLabel = () => new Date().toLocaleDateString('en-IN', {
  month: 'long',
  year: 'numeric',
});

const buildStatutoryPreview = (statutorySettings, basicMonthly, grossMonthly, pfApplicableChoice) => {
  const pfConfig = statutorySettings?.pf || {};
  const esiConfig = statutorySettings?.esi || {};

  const pfEnabled = pfConfig.enabled !== false;
  const pfRate = Number(pfConfig.employee_rate || 12);
  const pfEmployerEpfRate = Number(pfConfig.employer_epf_rate || 3.67);
  const pfEmployerEpsRate = Number(pfConfig.employer_eps_rate || 8.33);
  const pfThreshold = Number(pfConfig.wage_cap || 15000);
  const pfEpsCap = Number(pfConfig.eps_cap || pfThreshold || 15000);
  const pfAutoApplied = basicMonthly > 0 && basicMonthly <= pfThreshold;
  const pfSelectable = basicMonthly > pfThreshold;
  const pfApplied = pfEnabled && (pfAutoApplied || (pfSelectable && !!pfApplicableChoice));
  const pfUsesFullSalary = pfApplied && pfSelectable && !!pfApplicableChoice;
  const pfBase = pfApplied
    ? (pfUsesFullSalary ? basicMonthly : Math.min(basicMonthly, pfThreshold || basicMonthly))
    : 0;
  const pfEmployerEpsBase = pfApplied ? Math.min(basicMonthly, pfEpsCap || basicMonthly) : 0;
  const pfMonthly = pfApplied ? (pfBase * pfRate) / 100 : 0;
  const pfEmployerTotalMonthly = pfApplied
    ? (pfBase * (pfEmployerEpfRate + pfEmployerEpsRate)) / 100
    : 0;
  const pfEmployerEpsMonthly = pfApplied ? (pfEmployerEpsBase * pfEmployerEpsRate) / 100 : 0;
  const pfEmployerEpfMonthly = pfApplied
    ? Math.max(pfEmployerTotalMonthly - pfEmployerEpsMonthly, 0)
    : 0;

  const esiEnabled = esiConfig.enabled !== false;
  const esiRate = Number(esiConfig.employee_rate || 0.75);
  const esiEmployerRate = Number(esiConfig.employer_rate || 3.25);
  const esiThreshold = Number(esiConfig.eligibility_threshold || 21000);
  const esiApplied = esiEnabled && grossMonthly > 0 && grossMonthly <= esiThreshold;
  const esiMonthly = esiApplied ? (grossMonthly * esiRate) / 100 : 0;
  const esiEmployerMonthly = esiApplied ? (grossMonthly * esiEmployerRate) / 100 : 0;

  return {
    pf: {
      enabled: pfEnabled,
      rate: pfRate,
      threshold: pfThreshold,
      eps_cap: pfEpsCap,
      uses_full_salary: pfUsesFullSalary,
      employee_base: pfBase,
      is_auto_applied: pfAutoApplied,
      is_selectable: pfSelectable,
      is_applied: pfApplied,
      monthly_amount: pfMonthly,
      yearly_amount: pfMonthly * 12,
      employer_epf_rate: pfEmployerEpfRate,
      employer_eps_rate: pfEmployerEpsRate,
      employer_epf_monthly: pfEmployerEpfMonthly,
      employer_epf_yearly: pfEmployerEpfMonthly * 12,
      employer_eps_monthly: pfEmployerEpsMonthly,
      employer_eps_yearly: pfEmployerEpsMonthly * 12,
      employer_total_monthly: pfEmployerEpfMonthly + pfEmployerEpsMonthly,
      employer_total_yearly: (pfEmployerEpfMonthly + pfEmployerEpsMonthly) * 12,
    },
    esi: {
      enabled: esiEnabled,
      rate: esiRate,
      threshold: esiThreshold,
      is_applied: esiApplied,
      monthly_amount: esiMonthly,
      yearly_amount: esiMonthly * 12,
      employer_rate: esiEmployerRate,
      employer_monthly_amount: esiEmployerMonthly,
      employer_yearly_amount: esiEmployerMonthly * 12,
    },
  };
};

function EditSalary() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [allowances, setAllowances] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [statutorySettings, setStatutorySettings] = useState(null);

  const [formData, setFormData] = useState({
    emp_id: userId,
    basic_yearly: 0,
    basic_monthly: 0,
    ctc_yearly: 0,
    ctc_monthly: 0,
    gross_yearly: 0,
    gross_monthly: 0,
    all_allowances: [],
    all_deductions: [],
    pf_applicable: false,
    statutory_preview: { pf: {}, esi: {} },
  });

  const versionMonth = useMemo(() => monthLabel(), []);

  const recalculateTotals = useCallback(({ basicYearly, allAllowances, allDeductions, pfApplicable, settings = statutorySettings }) => {
    const safeBasicYearly = Number(basicYearly || 0);
    const basicMonthly = safeBasicYearly / 12;
    const totalAllowancesYearly = allAllowances.reduce((sum, item) => sum + Number(item.allowance_yearly || 0), 0);
    const grossYearly = safeBasicYearly + totalAllowancesYearly;
    const grossMonthly = grossYearly / 12;
    const preview = buildStatutoryPreview(settings, basicMonthly, grossMonthly, pfApplicable);
    const normalizedPfApplicable = preview.pf.is_auto_applied ? true : pfApplicable;
    const ctcYearly =
      grossYearly +
      Number(preview.pf.employer_total_yearly || 0) +
      Number(preview.esi.employer_yearly_amount || 0);

    return {
      basic_yearly: safeBasicYearly,
      basic_monthly: basicMonthly,
      gross_yearly: grossYearly,
      gross_monthly: grossMonthly,
      ctc_yearly: ctcYearly,
      ctc_monthly: ctcYearly / 12,
      all_allowances: allAllowances,
      all_deductions: allDeductions,
      pf_applicable: normalizedPfApplicable,
      statutory_preview: preview,
    };
  }, [statutorySettings]);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await api.get(`/employee-pay-structure/${userId}`, {
          timeout: 20000,
        });
        if (response.data.success) {
          const data = response.data.data;
          setEmployeeData(data.emp_detail);
          setAllowances(data.allowances || []);
          setDeductions(data.deductions || []);

          const currentStatutorySettings = data.statutory_settings || null;
          setStatutorySettings(currentStatutorySettings);

          const initialAllowances = (data.allowances || []).map((allowance) => ({
            label_id: allowance.label_id,
            allowance_yearly: Number(allowance.flat_amount || 0),
            allowance_monthly: Number(allowance.flat_amount || 0) / 12,
          }));

          const initialDeductions = (data.deductions || []).map((deduction) => ({
            label_id: deduction.label_id,
            deduction_yearly: Number(deduction.flat_amount || 0),
            deduction_monthly: Number(deduction.flat_amount || 0) / 12,
          }));

          const safeBasicYearly = Number(data.emp_paystructure?.basic_yearly || 0);
          const basicMonthly = safeBasicYearly / 12;
          const totalAllowancesYearly = initialAllowances.reduce(
            (sum, item) => sum + Number(item.allowance_yearly || 0),
            0,
          );
          const totalDeductionsYearly = initialDeductions.reduce(
            (sum, item) => sum + Number(item.deduction_yearly || 0),
            0,
          );
          const grossYearly = safeBasicYearly + totalAllowancesYearly;
          const grossMonthly = grossYearly / 12;
          const preview = buildStatutoryPreview(
            currentStatutorySettings,
            basicMonthly,
            grossMonthly,
            !!data.emp_paystructure?.pf_applicable,
          );

          setFormData((prev) => ({
            ...prev,
            basic_yearly: safeBasicYearly,
            basic_monthly: basicMonthly,
            gross_yearly: grossYearly,
            gross_monthly: grossMonthly,
            ctc_yearly:
              grossYearly + totalDeductionsYearly + preview.pf.yearly_amount + preview.esi.yearly_amount,
            ctc_monthly:
              (grossYearly + totalDeductionsYearly + preview.pf.yearly_amount + preview.esi.yearly_amount) / 12,
            all_allowances: initialAllowances,
            all_deductions: initialDeductions,
            pf_applicable: preview.pf.is_auto_applied
              ? true
              : !!data.emp_paystructure?.pf_applicable,
            statutory_preview: preview,
          }));
        }
      } catch (error) {
        const message = error.code === 'ECONNABORTED'
          ? 'Employee salary data is taking too long to load. Please try again.'
          : (error.response?.data?.message || 'Failed to load employee data');
        Swal.fire({ icon: 'error', title: 'Error', text: message });
        navigate('/salary');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [navigate, userId]);

  const updateAllowance = (labelId, yearlyValue) => {
    const value = Number(yearlyValue || 0);
    const updatedAllowances = formData.all_allowances.map((item) =>
      item.label_id === labelId
        ? { ...item, allowance_yearly: value, allowance_monthly: value / 12 }
        : item,
    );

    setFormData((prev) => ({
      ...prev,
      ...recalculateTotals({
        basicYearly: prev.basic_yearly,
        allAllowances: updatedAllowances,
        allDeductions: prev.all_deductions,
        pfApplicable: prev.pf_applicable,
      }),
    }));
  };

  const updateDeduction = (labelId, yearlyValue) => {
    const value = Number(yearlyValue || 0);
    const updatedDeductions = formData.all_deductions.map((item) =>
      item.label_id === labelId
        ? { ...item, deduction_yearly: value, deduction_monthly: value / 12 }
        : item,
    );

    setFormData((prev) => ({
      ...prev,
      ...recalculateTotals({
        basicYearly: prev.basic_yearly,
        allAllowances: prev.all_allowances,
        allDeductions: updatedDeductions,
        pfApplicable: prev.pf_applicable,
      }),
    }));
  };

  const updateBasic = (yearlyValue) => {
    setFormData((prev) => ({
      ...prev,
      ...recalculateTotals({
        basicYearly: yearlyValue,
        allAllowances: prev.all_allowances,
        allDeductions: prev.all_deductions,
        pfApplicable: prev.pf_applicable,
      }),
    }));
  };

  const togglePfApplicability = (checked) => {
    setFormData((prev) => ({
      ...prev,
      ...recalculateTotals({
        basicYearly: prev.basic_yearly,
        allAllowances: prev.all_allowances,
        allDeductions: prev.all_deductions,
        pfApplicable: checked,
      }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.basic_yearly || Number(formData.basic_yearly) <= 0) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Please enter a valid basic yearly salary' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        emp_id: userId,
        basic_yearly: Number(formData.basic_yearly),
        basic_monthly: Number(formData.basic_monthly),
        ctc_yearly: Number(formData.ctc_yearly),
        ctc_monthly: Number(formData.ctc_monthly),
        gross_yearly: Number(formData.gross_yearly),
        gross_monthly: Number(formData.gross_monthly),
        all_allowances: formData.all_allowances,
        all_deductions: formData.all_deductions,
        pf_applicable: !!formData.pf_applicable,
      };

      const response = await api.put(`/employee-pay-structure/${userId}`, payload);
      if (response.data.success) {
        Swal.fire({ icon: 'success', title: 'Success!', text: response.data.message, timer: 2000, showConfirmButton: false });
        navigate('/salary');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update pay structure',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <MainLayout><div className="loading-container"><div className="spinner"></div><p>Loading employee data...</p></div></MainLayout>;
  }

  const pfPreview = formData.statutory_preview.pf || {};
  const esiPreview = formData.statutory_preview.esi || {};

  return (
    <MainLayout>
      <div className="salary-form-page salary-form-shell">
        <div className="salary-form-hero">
          <div className="salary-form-hero-copy">
            <span className="salary-advances-eyebrow">Salary structure</span>
            <h1>Create Salary Revision</h1>
            <p>
              Create a fresh salary revision for {employeeData?.first_name}{" "}
              {employeeData?.last_name}. Annual edits automatically flow into
              monthly values and statutory deduction previews.
            </p>
          </div>
          <div className="salary-form-hero-badge">
            <span>Version month</span>
            <strong>{versionMonth}</strong>
          </div>
        </div>

        <div className="salary-form-card">
          <div className="salary-form-card-header">
            <div>
              <span className="section-kicker">Compensation builder</span>
              <h4 className="form-title">Create Salary Revision - {employeeData?.first_name} {employeeData?.last_name}</h4>
            </div>
          </div>
          <p className="text-muted mb-4 salary-form-intro">
            Salary versions are now month-versioned automatically. If multiple revisions are created in the same month,
            the latest one is used in the next payroll cycle.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="salary-form-section">
              <div className="salary-table-header">
                <div className="col-label"></div>
                <div className="col-percent"></div>
                <div className="col-value">Monthly</div>
                <div className="col-value">Annually</div>
              </div>

              <div className="salary-row">
                <div className="col-label"><label>Basic <span className="required">*</span></label></div>
                <div className="col-percent"></div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(formData.basic_monthly)} readOnly /></div>
                <div className="col-value"><input type="number" className="form-control" value={formData.basic_yearly} onChange={(e) => updateBasic(e.target.value)} /></div>
              </div>
            </div>

            {allowances.length > 0 && <>
              <div className="salary-form-section">
                <div className="section-divider"><h6>Allowances</h6></div>
                <div className="salary-table-header">
                  <div className="col-label">Allowances</div>
                  <div className="col-percent">%</div>
                  <div className="col-value">Monthly</div>
                  <div className="col-value">Annually</div>
                </div>
                {allowances.map((allowance) => {
                  const allowanceData = formData.all_allowances.find((item) => item.label_id === allowance.label_id) || { allowance_monthly: 0, allowance_yearly: 0 };
                  const isPercentBased = Number(allowance.label_detail?.value_in_percent || 0) > 0;
                  return (
                    <div key={allowance.label_id} className="salary-row allowance-row">
                      <div className="col-label">
                        <div className="salary-item-copy">
                          <span className="salary-item-title">{allowance.label_detail.title}</span>
                          <small className="salary-item-note">
                            {isPercentBased
                              ? `Auto-calculated on ${allowance.label_detail?.benefits_on || 'Basic'} at ${allowance.label_detail.value_in_percent}%.`
                              : 'Manual yearly allowance amount.'}
                          </small>
                        </div>
                      </div>
                      <div className="col-percent">{isPercentBased ? <span className="rule-pill">{allowance.label_detail.value_in_percent}%</span> : ''}</div>
                      <div className="col-value"><input type="text" className="form-control" value={formatCurrency(allowanceData.allowance_monthly)} readOnly /></div>
                      <div className="col-value"><input type="number" className="form-control" value={allowanceData.allowance_yearly} onChange={(e) => updateAllowance(allowance.label_id, e.target.value)} readOnly={isPercentBased} /></div>
                    </div>
                  );
                })}
              </div>
            </>}

            <div className="salary-form-section">
              <div className="section-divider"></div>
              <div className="salary-row highlight">
                <div className="col-label"><strong>Gross Salary</strong></div>
                <div className="col-percent"></div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(formData.gross_monthly)} readOnly /></div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(formData.gross_yearly)} readOnly /></div>
              </div>
            </div>

            <div className="salary-form-section">
              <div className="section-divider"><h6>Statutory Deductions</h6></div>
              <div className="salary-table-header">
                <div className="col-label">Deduction</div>
                <div className="col-percent">Rule</div>
                <div className="col-value">Monthly</div>
                <div className="col-value">Annually</div>
              </div>
              <div className="salary-row statutory-row">
                <div className="col-label">
                  <div className="statutory-label-wrap">
                    <span>Provident Fund</span>
                    {pfPreview.is_selectable && (
                      <label className="statutory-checkbox">
                        <input type="checkbox" checked={!!formData.pf_applicable} onChange={(e) => togglePfApplicability(e.target.checked)} />
                        Apply PF
                      </label>
                    )}
                  </div>
                  <small className="statutory-note">
                    {pfPreview.is_auto_applied
                      ? `Auto applied because basic is within threshold of ${formatCurrency(pfPreview.threshold)}.`
                      : pfPreview.is_selectable
                        ? `Basic is above threshold of ${formatCurrency(pfPreview.threshold)}. If selected, PF is calculated on full basic salary.`
                        : 'PF is disabled in statutory settings.'}
                  </small>
                </div>
                <div className="col-percent">{pfPreview.enabled ? `${pfPreview.rate || 0}%` : 'Disabled'}</div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(pfPreview.monthly_amount)} readOnly /></div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(pfPreview.yearly_amount)} readOnly /></div>
              </div>
              <div className="salary-row statutory-row">
                <div className="col-label">
                  <span>Employee State Insurance</span>
                  <small className="statutory-note">
                    {esiPreview.is_applied
                      ? `Auto applied because gross is within threshold of ${formatCurrency(esiPreview.threshold)}.`
                      : `Not applied because gross is above threshold of ${formatCurrency(esiPreview.threshold)}.`}
                  </small>
                </div>
                <div className="col-percent">{esiPreview.enabled ? `${esiPreview.rate || 0}%` : 'Disabled'}</div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(esiPreview.monthly_amount)} readOnly /></div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(esiPreview.yearly_amount)} readOnly /></div>
              </div>
            </div>

            {deductions.length > 0 && <>
              <div className="salary-form-section">
                <div className="section-divider"><h6>Other Deductions</h6></div>
                <div className="salary-table-header">
                  <div className="col-label">Deductions</div>
                  <div className="col-percent">%</div>
                  <div className="col-value">Monthly</div>
                  <div className="col-value">Annually</div>
                </div>
                {deductions.map((deduction) => {
                  const deductionData = formData.all_deductions.find((item) => item.label_id === deduction.label_id) || { deduction_monthly: 0, deduction_yearly: 0 };
                  const isPercentBased = Number(deduction.label_detail?.value_in_percent || 0) > 0;
                  return (
                    <div key={deduction.label_id} className="salary-row deduction-row">
                      <div className="col-label">
                        <div className="salary-item-copy">
                          <span className="salary-item-title">{deduction.label_detail.title}</span>
                          <small className="salary-item-note">
                            {isPercentBased
                              ? `Auto-calculated on ${deduction.label_detail?.benefits_on || 'Gross'} at ${deduction.label_detail.value_in_percent}%.`
                              : 'Manual yearly deduction amount.'}
                          </small>
                        </div>
                      </div>
                      <div className="col-percent">{isPercentBased ? <span className="rule-pill">{deduction.label_detail.value_in_percent}%</span> : ''}</div>
                      <div className="col-value"><input type="text" className="form-control" value={formatCurrency(deductionData.deduction_monthly)} readOnly /></div>
                      <div className="col-value"><input type="number" className="form-control" value={deductionData.deduction_yearly} onChange={(e) => updateDeduction(deduction.label_id, e.target.value)} readOnly={isPercentBased} /></div>
                    </div>
                  );
                })}
              </div>
            </>}

            <div className="salary-form-section">
              <div className="section-divider"></div>
              <div className="salary-row highlight">
                <div className="col-label"><strong>CTC (Cost to Company)</strong></div>
                <div className="col-percent"></div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(formData.ctc_monthly)} readOnly /></div>
                <div className="col-value"><input type="text" className="form-control" value={formatCurrency(formData.ctc_yearly)} readOnly /></div>
              </div>
            </div>

            <div className="salary-version-note">
              <strong>Version Month:</strong> {versionMonth}
              <span>This is auto-managed now. The latest salary version created in this month will be used in the next payroll cycle.</span>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => navigate('/salary')}>Cancel</button>
              <button type="submit" className="btn-submit" disabled={submitting}>{submitting ? 'Creating Revision...' : 'Create Salary Revision'}</button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
}

export default EditSalary;
